if (!customElements.get('delivery-checkout-gate')) {
  class DeliveryCheckoutGate extends HTMLElement {
    connectedCallback() {
      if (this.parentElement !== document.body) {
        const activeGate = document.body.querySelector(':scope > delivery-checkout-gate');
        if (activeGate && activeGate !== this) {
          activeGate.syncWithCart();
          this.remove();
          return;
        }
        document.body.appendChild(this);
        return;
      }
      if (this.initialized) return;
      this.initialized = true;
      this.form = document.getElementById('cart');
      this.checkoutButton = null;
      this.backdrop = this.querySelector('[data-delivery-gate]');
      this.dialog = this.querySelector('.delivery-gate__dialog');
      this.options = Array.from(this.querySelectorAll('[data-delivery-method]'));
      this.message = this.querySelector('[data-delivery-gate-message]');
      this.pickupPanel = this.querySelector('[data-delivery-gate-pickup]');
      this.pickupOpenButton = this.querySelector('[data-delivery-pickup-open]');
      this.localPanel = this.querySelector('[data-delivery-gate-local]');
      this.continueButton = this.querySelector('[data-delivery-gate-continue]');
      this.belowMinimum = this.dataset.tkBelowMinimum === 'true';
      this.hasFrozenProducts = this.dataset.hasTkProducts === 'true';
      this.selectedMethod = '';
      this.lastFocusedElement = null;
      this.checkoutClickHandler = () => this.open();

      this.bindCheckoutButton();
      this.options.forEach((option) => option.addEventListener('click', () => this.select(option.dataset.deliveryMethod)));
      this.querySelector('[data-delivery-gate-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-delivery-gate-cancel]')?.addEventListener('click', () => this.close());
      this.continueButton?.addEventListener('click', () => this.continueToCheckout());
      this.pickupOpenButton?.addEventListener('click', () => this.openPickup());
      this.backdrop?.addEventListener('click', (event) => {
        if (event.target === this.backdrop) this.close();
      });
      this.addEventListener('keydown', (event) => this.handleKeydown(event));
      document.addEventListener('pickup:change', () => this.update());
      this.observeCartChanges();
    }

    bindCheckoutButton() {
      const currentButton = document.getElementById('CheckOut');
      if (currentButton === this.checkoutButton) return;

      this.checkoutButton?.removeEventListener('click', this.checkoutClickHandler);
      this.checkoutButton = currentButton;
      this.checkoutButton?.addEventListener('click', this.checkoutClickHandler);
      this.form = document.getElementById('cart');
    }

    observeCartChanges() {
      const subtotal = document.getElementById('AjaxCartSubtotal');
      if (!subtotal || this.subtotalObserver) return;

      this.subtotalObserver = new MutationObserver(() => this.syncWithCart());
      this.subtotalObserver.observe(subtotal, { childList: true, subtree: true });
    }

    syncWithCart() {
      this.bindCheckoutButton();
      this.refreshFrozenState();
    }

    open() {
      if (!this.backdrop) return;
      this.lastFocusedElement = document.activeElement;
      this.backdrop.hidden = false;
      document.documentElement.classList.add('delivery-gate-open');
      this.dialog?.focus();
      this.refreshFrozenState();
    }

    close() {
      if (!this.backdrop) return;
      this.backdrop.hidden = true;
      document.documentElement.classList.remove('delivery-gate-open');
      this.clearMessage();
      this.lastFocusedElement?.focus?.();
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.backdrop && !this.backdrop.hidden) {
        event.preventDefault();
        this.close();
      }
    }

    select(method) {
      this.selectedMethod = method;
      this.options.forEach((option) => {
        const selected = option.dataset.deliveryMethod === method;
        option.classList.toggle('delivery-gate__option--selected', selected);
        option.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      if (this.pickupPanel) this.pickupPanel.hidden = method !== 'pickup';
      if (this.localPanel) this.localPanel.hidden = method !== 'local';
      if (method !== 'pickup') this.clearPickup();
      this.clearMessage();
      this.update();
    }

    getPickupPicker() {
      return this.querySelector('pickup-date-picker');
    }

    pickupIsReady() {
      const picker = this.getPickupPicker();
      const toggle = picker?.querySelector('[data-pickup-toggle]');
      const date = picker?.querySelector('[data-pickup-date]');
      const time = picker?.querySelector('[data-pickup-time]');
      return Boolean(toggle?.checked && date?.value && time?.value);
    }

    openPickup() {
      const picker = this.getPickupPicker();
      const toggle = picker?.querySelector('[data-pickup-toggle]');
      const summaryButton = picker?.querySelector('[data-pickup-summary-btn]');
      if (toggle && !toggle.checked) {
        toggle.click();
      } else {
        summaryButton?.click();
      }
    }

    clearPickup() {
      const picker = this.getPickupPicker();
      const toggle = picker?.querySelector('[data-pickup-toggle]');
      if (toggle?.checked) toggle.click();
    }

    update() {
      if (!this.continueButton) return;
      const pickupReady = this.pickupIsReady();
      const hasChoice = Boolean(this.selectedMethod);
      this.continueButton.disabled = !hasChoice || (this.selectedMethod === 'pickup' && !pickupReady);
      if (this.pickupOpenButton) {
        this.pickupOpenButton.textContent = pickupReady ? 'Abholtermin ändern' : 'Abholtermin auswählen';
      }
    }

    showMessage(message) {
      if (!this.message) return;
      this.message.textContent = message;
      this.message.hidden = false;
    }

    clearMessage() {
      if (!this.message) return;
      this.message.textContent = '';
      this.message.hidden = true;
    }

    async continueToCheckout() {
      if (!this.selectedMethod) {
        this.showMessage('Bitte wähle zuerst eine Zustellart.');
        return;
      }

      await this.refreshFrozenState();

      if (this.selectedMethod === 'shipping' && this.hasFrozenProducts && this.belowMinimum) {
        this.showMessage(`Für UPS Express-Versand sind mindestens ${this.dataset.tkMinimum} kg Frostware erforderlich. Bitte ergänze Frostware oder wähle Abholung beziehungsweise lokalen Lieferservice.`);
        return;
      }

      if (this.selectedMethod === 'pickup' && !this.pickupIsReady()) {
        this.showMessage('Bitte wähle für die Abholung einen Termin mit Uhrzeit.');
        return;
      }

      this.continueButton.disabled = true;
      try {
        await this.saveChoice();
        this.submitCheckout();
      } catch (error) {
        this.continueButton.disabled = false;
        this.showMessage('Deine Auswahl konnte nicht gespeichert werden. Bitte versuche es erneut.');
      }
    }

    async refreshFrozenState() {
      try {
        const response = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const cart = await response.json();
        const minimum = Number(this.dataset.tkMinimum) || 10;
        let frozenWeight = 0;
        let hasFrozenProducts = false;

        cart.items.forEach((item) => {
          const key = String(item.key || '').replace(/"/g, '\\\"');
          const lineItem = key
            ? document.querySelector('[data-line-item-key="' + key + '"]')
            : null;

          if (lineItem?.dataset.isTk === 'true') {
            hasFrozenProducts = true;
            frozenWeight += ((item.grams || 0) * (item.quantity || 0)) / 1000;
          }
        });

        this.hasFrozenProducts = hasFrozenProducts;
        this.belowMinimum = hasFrozenProducts && frozenWeight < minimum;
      } catch (error) {
        // Die serverseitige Checkout-Prüfung bleibt die verbindliche Absicherung.
      }
    }

    async saveChoice() {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            Lieferart: this.selectedMethod,
            'Lieferservice-PLZ': '',
          },
        }),
      });
      if (!response.ok) throw new Error(`cart update failed: ${response.status}`);
    }

    submitCheckout() {
      if (!this.form) return;
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '';
      this.form.appendChild(checkoutInput);
      this.form.submit();
    }
  }

  customElements.define('delivery-checkout-gate', DeliveryCheckoutGate);
}
